/**
 * CompanySharePanel — iter290
 * ────────────────────────────────────────────────────────────────
 * Marketing bundle for a verified CARTAL business profile.
 * Anyone (owner OR visitor) can:
 *   • Share the profile via WhatsApp / X / Telegram / Facebook / Email
 *   • Copy a canonical direct link (with UTM = "profile_share")
 *   • Copy a ready-to-paste HTML embed snippet (a card that links back
 *     to the CARTAL profile) so the owner can drop it in their own
 *     website / blog / signature.
 *   • Open / copy / share a Google Maps deep-link to the business
 *     location, built from `city` + `address_ar`.
 *   • Download a printable QR image (PNG) that decodes back to the
 *     canonical profile URL — for flyers, business cards, receipts.
 *
 * The panel is purely presentational and reuses the existing
 * <ShareBar/> so we never diverge from the marketplace share style.
 */
import React, { useMemo, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Check, Copy, Download, MapPin, ExternalLink, Code2, QrCode } from "lucide-react";
import ShareBar from "../ShareBar";
import { useI18n } from "../../lib/i18n";

function buildEmbedSnippet({ url, nameAr, nameEn, city }) {
  const displayName = (nameAr || nameEn || "CARTAL Business").replace(/</g, "&lt;");
  const cityLine = city ? `<div style="font-size:12px;color:#8a8a8a">${String(city).replace(/</g, "&lt;")}</div>` : "";
  // Kept as a single line-broken string for easy copy / paste. Uses
  // inline styles so it drops into any host page (including plain
  // HTML sites) without a stylesheet dependency.
  return `<a href="${url}" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:12px;padding:12px 16px;border-radius:14px;border:1px solid rgba(201,165,92,0.35);background:#fafaf7;color:#0a0a0a;text-decoration:none;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;max-width:360px">
  <span style="display:inline-flex;width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#e8c873,#c9a55c);align-items:center;justify-content:center;font-weight:700">C</span>
  <span style="display:flex;flex-direction:column;min-width:0">
    <span style="font-weight:700;font-size:14px">${displayName}</span>
    ${cityLine}
    <span style="font-size:11px;color:#8a6f2e;margin-top:2px">Verified on CARTAL · تحقق على كارتال</span>
  </span>
</a>`;
}

function mapsUrlFor({ city, address, name }) {
  const parts = [name, address, city, "Saudi Arabia"].filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
}

function useCopy() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  };
  return { copied, copy };
}

export default function CompanySharePanel({ company }) {
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const canonicalUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    // Strip query + hash for a clean share link; keep origin + pathname.
    const { origin, pathname } = window.location;
    const clean = `${origin}${pathname}`;
    // Attach a profile_share UTM so the seller's shares are attributable
    // and never confused with organic traffic.
    return `${clean}?utm_source=profile&utm_medium=share&utm_campaign=profile_share`;
  }, []);

  // iter330 — QR must be its own independent traffic source (Analytics
  // spec: "QR" is listed as a distinct channel). The share URL used by
  // <ShareBar/> is left untouched (per-channel utm_source is set by
  // buildUrl there), but the printable QR now encodes a URL that
  // carries utm_source=qr so every scan is attributed correctly and
  // not lumped into "profile".
  const qrUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const { origin, pathname } = window.location;
    return `${origin}${pathname}?utm_source=qr&utm_medium=qrcode&utm_campaign=profile_qr`;
  }, []);

  const shareText = isAr
    ? `مركز خدمة معتمد على كارتال — ${company.name_ar}`
    : `Verified CARTAL provider — ${company.name_en || company.name_ar}`;

  const mapsUrl = useMemo(() =>
    mapsUrlFor({
      city: company.city,
      address: company.address_ar,
      name: company.name_ar || company.name_en,
    }),
  [company.city, company.address_ar, company.name_ar, company.name_en]);

  const embedSnippet = useMemo(() => buildEmbedSnippet({
    url: canonicalUrl,
    nameAr: company.name_ar,
    nameEn: company.name_en,
    city: company.city,
  }), [canonicalUrl, company.name_ar, company.name_en, company.city]);

  const embedCopy = useCopy();
  const mapsCopy = useCopy();

  const qrRef = useRef(null);
  // iter329 — Download must work on Safari/iOS too. Fixes:
  //   1) The old code created a detached <a> — Safari refuses to
  //      trigger a download unless the anchor is attached to the DOM.
  //   2) `canvas.toDataURL()` produces very large data-URLs which
  //      Safari opens in a new tab instead of downloading. Using a
  //      Blob + URL.createObjectURL is the WebKit-friendly path.
  //   3) File name unified to `CARTAL-QR.png` per product spec.
  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const filename = "CARTAL-QR.png";
    const trigger = (href, revoke) => {
      const a = document.createElement("a");
      a.href = href;
      a.download = filename;
      a.rel = "noopener";
      // Anchor MUST be in the DOM for iOS Safari to honour `download`.
      document.body.appendChild(a);
      a.click();
      // Cleanup on next tick so the browser has time to start the
      // download before we tear the URL down.
      setTimeout(() => {
        try { a.remove(); } catch (_e) { /* ignore */ }
        if (revoke) { try { URL.revokeObjectURL(href); } catch (_e) { /* ignore */ } }
      }, 1000);
    };
    // Preferred: canvas.toBlob → Blob URL (small, WebKit-friendly).
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob((blob) => {
        if (blob) {
          trigger(URL.createObjectURL(blob), true);
        } else {
          // Fallback if the browser can't produce a Blob for this canvas.
          trigger(canvas.toDataURL("image/png"), false);
        }
      }, "image/png");
      return;
    }
    // Legacy fallback — same as before but with anchor attached to DOM.
    trigger(canvas.toDataURL("image/png"), false);
  };

  const shareQrNative = async () => {
    if (typeof navigator === "undefined" || !navigator.share) return;
    try {
      await navigator.share({ title: shareText, text: shareText, url: canonicalUrl });
    } catch {
      /* user cancelled */
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-[#0a0a0a]/[0.06] p-5 sm:p-6 space-y-6"
      data-testid="company-share-panel"
    >
      <div>
        <div className="text-[11px] uppercase tracking-widest font-bold text-[#8a6f2e] mb-1">
          {isAr ? "ساعد مركزك يوصل لكل السعودية" : "Help your centre reach all of Saudi"}
        </div>
        <h3 className="font-display text-lg text-[#0a0a0a]">
          {isAr ? "شارك صفحتك — أدوات جاهزة للتسويق" : "Share your profile — ready-made marketing tools"}
        </h3>
        <p className="text-xs text-[#525252] mt-1.5 leading-relaxed">
          {isAr
            ? "استخدم القنوات أو انسخ الكود أو حمّل رمز QR وضعه على الفواتير والبطاقات — كارتال يدعم شركاءه المعتمدين مجاناً."
            : "Use the channels, copy the embed code, or download the QR to print on invoices and cards — free marketing kit for verified CARTAL partners."}
        </p>
      </div>

      {/* 1) Social share bar (reuses the marketplace-standard component) */}
      <ShareBar
        url={canonicalUrl}
        title={shareText}
        city={company.city}
        vehicleId={company.id}
      />

      {/* 2) Location share */}
      <div className="rounded-2xl border border-[#0a0a0a]/10 bg-[#fafaf7] p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="overline overline-gold inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            {isAr ? "مشاركة الموقع على الخريطة" : "Share the map location"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
            data-testid="share-maps-open"
          >
            <ExternalLink className="w-4 h-4 text-[#8a6f2e]" />
            {isAr ? "افتح في خرائط قوقل" : "Open in Google Maps"}
          </a>
          <button
            type="button"
            onClick={() => mapsCopy.copy(mapsUrl)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
            data-testid="share-maps-copy"
          >
            {mapsCopy.copied
              ? <Check className="w-4 h-4 text-green-600" />
              : <Copy className="w-4 h-4 text-[#525252]" />}
            {mapsCopy.copied
              ? (isAr ? "تم النسخ" : "Copied")
              : (isAr ? "نسخ رابط الموقع" : "Copy map link")}
          </button>
          {typeof navigator !== "undefined" && navigator.share && (
            <button
              type="button"
              onClick={() => navigator.share({ title: shareText, text: shareText, url: mapsUrl }).catch(() => {})}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
              data-testid="share-maps-native"
            >
              {isAr ? "مشاركة الخريطة" : "Share map"}
            </button>
          )}
        </div>
        {(company.address_ar || company.city) && (
          <div className="text-[11px] text-[#8a8a8a] mt-2" dir="auto">
            {[company.address_ar, company.city].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>

      {/* 3) Embed code */}
      <div className="rounded-2xl border border-[#0a0a0a]/10 bg-[#fafaf7] p-4">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="overline overline-gold inline-flex items-center gap-1.5">
            <Code2 className="w-3.5 h-3.5" />
            {isAr ? "كود جاهز للنشر على موقعك" : "Embed on your own site"}
          </div>
          <button
            type="button"
            onClick={() => embedCopy.copy(embedSnippet)}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
            data-testid="share-embed-copy"
          >
            {embedCopy.copied
              ? <Check className="w-4 h-4 text-green-600" />
              : <Copy className="w-4 h-4 text-[#525252]" />}
            {embedCopy.copied
              ? (isAr ? "تم النسخ" : "Copied")
              : (isAr ? "نسخ الكود" : "Copy code")}
          </button>
        </div>
        <textarea
          readOnly
          value={embedSnippet}
          onFocus={(e) => e.target.select()}
          rows={6}
          className="w-full text-[11px] font-mono bg-white border border-[#0a0a0a]/10 rounded-lg p-2.5 leading-relaxed"
          data-testid="share-embed-code"
          dir="ltr"
        />
        <p className="text-[10px] text-[#8a8a8a] mt-1.5 leading-relaxed">
          {isAr
            ? "الصق الكود في أي موقع HTML، سيظهر كبطاقة تحويل مباشرة لصفحتك على كارتال."
            : "Paste this in any HTML page — it renders as a call-out card linking back to your CARTAL profile."}
        </p>
      </div>

      {/* 4) QR code */}
      <div className="rounded-2xl border border-[#0a0a0a]/10 bg-[#fafaf7] p-4">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="overline overline-gold inline-flex items-center gap-1.5">
            <QrCode className="w-3.5 h-3.5" />
            {isAr ? "رمز QR للطباعة" : "Printable QR code"}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={downloadQr}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
              data-testid="share-qr-download"
            >
              <Download className="w-4 h-4 text-[#525252]" />
              {isAr ? "تحميل PNG" : "Download PNG"}
            </button>
            {typeof navigator !== "undefined" && navigator.share && (
              <button
                type="button"
                onClick={shareQrNative}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold bg-white border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] transition-colors"
                data-testid="share-qr-native"
              >
                {isAr ? "مشاركة" : "Share"}
              </button>
            )}
          </div>
        </div>
        <div className="flex items-start gap-4 flex-wrap">
          <div
            ref={qrRef}
            className="p-3 rounded-xl bg-white border border-[#0a0a0a]/10"
            data-testid="share-qr-canvas"
          >
            <QRCodeCanvas
              value={qrUrl}
              size={160}
              includeMargin={false}
              level="M"
              fgColor="#0a0a0a"
              bgColor="#ffffff"
            />
          </div>
          <div className="flex-1 min-w-[180px] text-xs text-[#525252] leading-relaxed">
            {isAr
              ? "اطبع الرمز على البطاقات، الفواتير، أو ضعه في مقر المركز — كل عميل يمسحه ينتقل مباشرة لصفحتك."
              : "Print it on business cards, receipts, or display it in-store — every scan goes straight to your CARTAL profile."}
            <div className="mt-2 text-[10px] font-mono text-[#8a6f2e] break-all" dir="ltr">
              {canonicalUrl}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
